import { useState } from "react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DebugField } from "~/components/assistant/messages/DebugField";
import { DebugSection } from "~/components/assistant/messages/DebugSection";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { DataValue } from "~/components/assistant/types";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { trackEvent } from "~/utils/monitoring";

interface DataValueMessageProps {
  value: DataValue;
  debugMode?: boolean;
}

type TabType = "value" | "code" | "lineage";

interface Tab {
  id: TabType;
  label: string;
}

// DataValueMessage component with tabbed interface
export function DataValueMessage({ value, debugMode = false }: DataValueMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("value");
  const { conversationId, contextId } = useAssistantContext();

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    if (contextId) {
      trackEvent("analysis_agent_artifact_tab_viewed", {
        dataset_id: contextId,
        conversation_id: conversationId || undefined,
        artifact_id: value.artifact_id || undefined,
        artifact_type: "value",
        context: "dataset",
        tab,
      });
    }
  };

  const tabs: Tab[] = [{ id: "value", label: "Value" }];
  if (value?.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (value?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 pt-1 w-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer ${
              activeTab === tab.id
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto">
        {activeTab === "value" && (
          <div>
            <div className="bg-white rounded-sm p-4">
              <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap break-words">
                {value.content}
              </pre>
            </div>
            {value?.summary && <div className="mt-3 text-xs text-gray-600">{value.summary}</div>}
          </div>
        )}

        {activeTab === "code" && value?.code && (
          <>
            {/* Code Header with Copy Button */}
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
              <CopyButton text={value.code} variant="light" size="sm" />
            </div>
            <div className="h-full overflow-hidden rounded-sm">
              {!isLoading && (
                <Highlight
                  theme={themes.dracula}
                  code={value.code}
                  language="python"
                  prism={PrismInstance as any}
                >
                  {({ style, tokens, getLineProps, getTokenProps, className }) => (
                    <pre
                      className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                        className || ""
                      }`}
                      style={style}
                    >
                      {tokens.map((line, i) => (
                        <div
                          {...getLineProps({ line, key: i })}
                          key={`line-${i}`}
                          style={{ minHeight: "1.5em" }}
                        >
                          {line.map((token, key) => (
                            <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              )}
              {isLoading && (
                <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
                  Loading syntax highlighting...
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "lineage" && value?.artifact_id && (
          <LineageTab artifactId={value.artifact_id} />
        )}
      </div>

      {/* Debug Section */}
      {debugMode && (
        <div className="mt-3 text-gray-800">
          <DebugSection
            content={
              <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200">
                <DebugField label="Value Data" content={value} />
              </div>
            }
            defaultExpanded={false}
          />
        </div>
      )}
    </div>
  );
}
